import { useEffect, useRef, useState, type ComponentProps } from 'react'
import { FieldInfo, hasFieldError, useFieldContext, useFormReadOnly, useIsFieldOptional } from './shared'
import { Input } from '../../ui/input'
import { FieldLabelRow } from './field-label-row'
import { EyeIcon, EyeOffIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { ButtonPrimitive } from '@/components/ui/button-primitive'
import { useLingui } from '@lingui/react/macro'

export default function PasswordField({
  label,
  className,
  optional,
  ...props
}: Omit<ComponentProps<'input'>, 'type'> & { label: string; optional?: boolean }) {
  const { t } = useLingui()
  const field = useFieldContext<string>()
  const formReadOnly = useFormReadOnly()
  const isReadOnly = props.readOnly ?? formReadOnly
  const isOptional = useIsFieldOptional(field.name, optional)
  const [showPassword, setShowPassword] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const value = field.state.value
  const displayValue = isReadOnly && !value ? '-' : value

  // A browser offers to store a credential only for a form that HAS a password field when it is submitted, so
  // a revealed field is re-masked on the way into submit — synchronously on the node, since React's own state
  // update lands a commit too late. Capture phase, so it runs before the form's React handler.
  useEffect(() => {
    const form = inputRef.current?.form
    if (!form) return
    const remask = () => {
      if (inputRef.current) inputRef.current.type = 'password'
      setShowPassword(false)
    }
    form.addEventListener('submit', remask, true)
    return () => form.removeEventListener('submit', remask, true)
  }, [])

  return (
    <div className={className}>
      <div className="grid gap-2">
        {label && (
          <FieldLabelRow
            label={label}
            htmlFor={field.name}
            hasError={field.state.meta.isTouched && !field.state.meta.isValid}
            optional={isOptional}
          />
        )}
        <div className="relative">
          <Input
            ref={inputRef}
            id={field.name}
            name={field.name}
            // Chrome 152 pins `-webkit-text-security` on a password input even against `!important`, so the
            // unmask-in-place trick no longer reveals anything; the type swap is the only reveal left, and
            // the submit listener above is what keeps it from costing the browser's save prompt.
            type={isReadOnly || showPassword ? 'text' : 'password'}
            aria-invalid={hasFieldError(field.state.meta)}
            value={displayValue}
            onBlur={field.handleBlur}
            onChange={(e) => field.handleChange(e.target.value)}
            {...props}
            readOnly={isReadOnly}
            tabIndex={isReadOnly ? -1 : props.tabIndex}
            className={cn(
              !isReadOnly && 'pr-10',
              isReadOnly &&
                'border-transparent bg-transparent px-0 focus-visible:border-transparent focus-visible:ring-0 dark:bg-transparent',
            )}
          />
          {!isReadOnly && (
            <ButtonPrimitive
              type="button"
              aria-label={showPassword ? t`Hide password` : t`Show password`}
              onClick={() => setShowPassword(!showPassword)}
              className="text-muted-foreground hover:text-foreground absolute top-1/2 right-3 -translate-y-1/2 cursor-pointer transition-colors"
              tabIndex={-1}
            >
              {showPassword ? <EyeOffIcon className="size-5" /> : <EyeIcon className="size-5" />}
            </ButtonPrimitive>
          )}
        </div>
      </div>
      <FieldInfo fieldState={field.state} />
    </div>
  )
}
